import { Injectable } from '@angular/core';
import { TextPatch } from '../templateGen/templateInterfaces';

export interface MatchResult {
  start: number;
  end: number;
  confidence: number;
}

@Injectable({ providedIn: 'root' })
export class StoryPatchService {

  /**
   * Apply a list of patches to the original story text to produce the concorded version.
   * Patches are applied from the end of the text backwards to preserve indices.
   */
  applyCumulativePatches(originalStory: string, patches: TextPatch[]): string {
    if (!patches.length) return originalStory;

    // Sort patches by position in text (find each match), then apply from end to start
    const positioned = patches
      .map(p => ({ patch: p, match: this.findBestMatch(p.original_text, originalStory) }))
      .filter(p => p.match !== null)
      .sort((a, b) => (b.match!.start) - (a.match!.start)); // reverse order

    let result = originalStory;
    for (const { patch, match } of positioned) {
      if (!match) continue;
      switch (patch.patch_type) {
        case 'append':
          result = result.slice(0, match.end) + ' ' + patch.enriched_text + result.slice(match.end);
          break;
        case 'replace':
          result = result.slice(0, match.start) + patch.enriched_text + result.slice(match.end);
          break;
        case 'annotate':
          // Annotate doesn't modify text — metadata only (rendered by UI)
          break;
      }
    }
    return result;
  }

  /**
   * Fuzzy match: find the best position of `source` within `storyText`.
   * Uses exact match first, then falls back to normalized matching.
   */
  findBestMatch(source: string, storyText: string): MatchResult | null {
    if (!source || !storyText) return null;

    // 1. Exact match
    const exactIdx = storyText.indexOf(source);
    if (exactIdx !== -1) {
      return { start: exactIdx, end: exactIdx + source.length, confidence: 1.0 };
    }

    // 2. Normalized match (strip extra whitespace, case-insensitive)
    const normalizedSource = this._normalize(source);
    const normalizedStory = this._normalize(storyText);
    const normIdx = normalizedStory.indexOf(normalizedSource);
    if (normIdx !== -1) {
      // Map back to original indices (approximate)
      const approxStart = this._mapNormalizedIndex(storyText, normIdx);
      const approxEnd = this._mapNormalizedIndex(storyText, normIdx + normalizedSource.length);
      return { start: approxStart, end: approxEnd, confidence: 0.8 };
    }

    // 3. Keyword fallback — find the sentence containing the most keywords
    const keywords = source.toLowerCase().split(/\s+/).filter(w => w.length > 3);
    if (keywords.length === 0) return null;

    const sentences = storyText.split(/[.!?\n]+/);
    let bestScore = 0;
    let bestSentenceStart = -1;
    let bestSentenceEnd = -1;
    let offset = 0;

    for (const sentence of sentences) {
      const sentLower = sentence.toLowerCase();
      const score = keywords.filter(k => sentLower.includes(k)).length / keywords.length;
      if (score > bestScore) {
        bestScore = score;
        bestSentenceStart = offset;
        bestSentenceEnd = offset + sentence.length;
      }
      offset += sentence.length + 1; // +1 for the delimiter
    }

    if (bestScore >= 0.5 && bestSentenceStart >= 0) {
      return { start: bestSentenceStart, end: bestSentenceEnd, confidence: bestScore * 0.6 };
    }

    return null;
  }

  /**
   * Check if two patches conflict (overlapping positions).
   */
  detectConflicts(patches: TextPatch[], storyText: string): Array<[TextPatch, TextPatch]> {
    const positioned = patches
      .map(p => ({ patch: p, match: this.findBestMatch(p.original_text, storyText) }))
      .filter(p => p.match !== null)
      .sort((a, b) => a.match!.start - b.match!.start);

    const conflicts: Array<[TextPatch, TextPatch]> = [];
    for (let i = 0; i < positioned.length - 1; i++) {
      const current = positioned[i];
      const next = positioned[i + 1];
      if (current.match!.end > next.match!.start) {
        conflicts.push([current.patch, next.patch]);
      }
    }
    return conflicts;
  }

  private _normalize(text: string): string {
    return text.toLowerCase().replace(/\s+/g, ' ').trim();
  }

  private _mapNormalizedIndex(original: string, normalizedIdx: number): number {
    let normCount = 0;
    let inWhitespace = false;
    for (let i = 0; i < original.length; i++) {
      if (/\s/.test(original[i])) {
        if (!inWhitespace) {
          normCount++;
          inWhitespace = true;
        }
      } else {
        normCount++;
        inWhitespace = false;
      }
      if (normCount >= normalizedIdx) return i + 1;
    }
    return original.length;
  }
}
